import { useParams, Link } from "react-router-dom";
import { getCategoryById, getArticlesByCategory, categories } from "@/mocks/data";
import SEO from "@/components/base/SEO";
import Breadcrumb from "@/components/base/Breadcrumb";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryById(slug || "");
  const categoryArticles = getArticlesByCategory(slug || "");

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-foreground-950 mb-2">分类未找到</h2>
          <Link to="/" className="text-primary-600 hover:underline text-sm cursor-pointer">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const seoData = {
    title: category.name,
    description: category.description,
    keywords: `${category.name},福利导航,${category.name}资源,${category.name}优惠,生活福利`,
    canonicalUrl: `https://jobbang.cn/category/${category.id}`,
  };

  return (
    <>
      <SEO data={seoData} />
      <main className="min-h-screen bg-background-50 pt-24 pb-12 md:pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: "首页", href: "/" },
              { label: category.name },
            ]}
          />

          <div className="mt-6 mb-8">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                <i className={`${category.icon} text-xl`}></i>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                {category.name}
              </h1>
              <span className="text-sm text-foreground-400 bg-background-100 px-2.5 py-1 rounded-md">
                {category.resourceCount} 项资源
              </span>
            </div>
            <p className="text-sm md:text-base text-foreground-600">{category.description}</p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-1">
              {categoryArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
                  {categoryArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.id}`}
                      className="group bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer overflow-hidden"
                    >
                      <div className="relative h-44 overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4 md:p-5">
                        <h3 className="text-sm md:text-base font-semibold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                          {article.title}
                        </h3>
                        <p className="text-xs md:text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center justify-between text-xs text-foreground-400">
                          <span>{article.date}</span>
                          <span>{article.readTime} 阅读</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                    <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-600">暂无相关内容，敬请期待</p>
                </div>
              )}
            </div>

            <aside className="w-full lg:w-64 flex-shrink-0">
              <div className="bg-background-100 rounded-lg p-4 md:p-5 sticky top-24">
                <h4 className="text-sm font-semibold text-foreground-950 mb-3">全部分类</h4>
                <div className="flex flex-col gap-1">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.id}`}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors cursor-pointer ${
                        cat.id === category.id
                          ? "bg-primary-100/70 text-primary-700 font-medium"
                          : "text-foreground-600 hover:bg-background-50 hover:text-foreground-950"
                      }`}
                    >
                      <i className={`${cat.icon} text-sm`}></i>
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
    </>
  );
}